import { dayDateOf, tt, type LunchDay } from '@agos/shared'

/**
 * How the printed week is laid out on paper - `FRONTEND.md` §9.377, FB-672.
 *
 * **The owner asked for *1:1* and this is the part that decides it.** Legacy prints the week as
 * **two A4 pages**: Monday to Wednesday on the first, the rest on the second, each with its own full
 * header and a label saying which days it carries. AGOS printed one continuous flow, so the sheets
 * came out of the printer as a different document from the one pinned up in the restaurant.
 *
 * A function rather than a `slice` in the JSX, because *which days, in which order, under which
 * label* is the whole of what 1:1 means here and it is the part that can be asserted without a
 * printer.
 */
export interface PrintWeekPage {
  days: LunchDay[]
  /** Legacy's own wording: *Pondelok – Streda*, *Štvrtok – Piatok*, *Štvrtok – Nedeľa*. */
  label: string
}

/**
 * The fixed order the printed week is read in.
 *
 * **Not `week.days` as they arrive.** Legacy walks `["mon"…]` and looks each one up, so a payload
 * that answered Wednesday first would still print Monday first - and the two-page split would
 * otherwise put an arbitrary three days on page one.
 */
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const
const WEEKEND = ['sat', 'sun'] as const

/** Where legacy breaks the page: three days on the first sheet, the rest on the second. */
const FIRST_PAGE_DAYS = 3

export function printWeekDays(
  days: readonly LunchDay[],
  showWeekends: boolean,
): LunchDay[] {
  const keys = showWeekends ? [...WEEKDAYS, ...WEEKEND] : [...WEEKDAYS]
  return keys
    .map((key) => days.find((day) => day.dayKey === key))
    .filter((day): day is LunchDay => day !== undefined)
}

export function printWeekPages(
  days: readonly LunchDay[],
  showWeekends: boolean,
): PrintWeekPage[] {
  const ordered = printWeekDays(days, showWeekends)
  const first = ordered.slice(0, FIRST_PAGE_DAYS)
  const rest = ordered.slice(FIRST_PAGE_DAYS)
  const pages: PrintWeekPage[] = []
  if (first.length > 0) {
    pages.push({ days: first, label: tt('Pondelok – Streda') })
  }
  if (rest.length > 0) {
    pages.push({
      days: rest,
      label: showWeekends ? tt('Štvrtok – Nedeľa') : tt('Štvrtok – Piatok'),
    })
  }
  return pages
}

/**
 * The date range in the header, in legacy's format - `21.09.2026 – 25.09.2026`.
 *
 * Zero-padded, and the end day follows `showWeekends` exactly as legacy's does: a week printed
 * without its weekend ends on Friday, and saying Sunday would date a sheet that does not carry it.
 */
export function printWeekRange(
  weekStartDate: string | undefined,
  showWeekends: boolean,
): string {
  const start = printFullDate(weekStartDate, 'mon')
  const end = printFullDate(weekStartDate, showWeekends ? 'sun' : 'fri')
  return start === '' || end === '' ? '' : `${start} – ${end}`
}

/** One day's calendar date as the sheet prints it: `21.09.2026`, or `''` on an undated week. */
export function printFullDate(weekStartDate: string | undefined, dayKey: string): string {
  const iso = dayDateOf(weekStartDate, dayKey)
  if (iso === null) return ''
  const [year, month, day] = iso.split('-')
  return `${day}.${month}.${year}`
}

/**
 * The logo the sheet carries, including legacy's oddity - §9.377.
 *
 * Legacy ignores `restaurantLogoUrl` when it equals its own bundled default and falls back to the
 * theme's. Copied rather than tidied: a franchise whose settings still hold that path gets their
 * theme logo on the sheet, which is what their current printouts look like.
 */
const LEGACY_DEFAULT_LOGO = '/images/logo-majstro.png'

export function printLogo(settingsLogo: string | undefined, themeLogo: string | null): string | null {
  const chosen = (settingsLogo ?? '').trim()
  if (chosen !== '' && chosen !== LEGACY_DEFAULT_LOGO) return chosen
  const fallback = (themeLogo ?? '').trim()
  return fallback === '' ? null : fallback
}

/**
 * How much a printed day has to shrink to stay on one sheet - §9.382, and legacy's own arithmetic.
 *
 * **§9.132 recorded this as unbuilt twice**: *"Legacy measures the rendered day against `1036 px` of
 * A4 content height and scales it by `A4 / height × 0.97` when it overflows, so a busy Wednesday
 * stays on one sheet instead of spilling a single dish onto a second page."* The constants are
 * legacy's, the `0.97` included - it is the slack that keeps a scaled sheet off the margin.
 *
 * A function because the **arithmetic** is assertable and the measurement is not: `scrollHeight`
 * needs a layout engine, and neither jsdom nor a print-media-less browser gives one. So the hidden
 * measuring div supplies the number and this decides what to do with it.
 */
/**
 * The width the hidden measuring sheet is laid out at - §9.382.
 *
 * A4 portrait is 210 mm; less legacy's 10 mm margins each side, at 96 dpi, that is **793 px** - and
 * 793 is the literal legacy uses, so this is its number rather than a recomputation of it. Measuring
 * at any other width measures a sheet nobody prints.
 */
export const PRINT_MEASURE_WIDTH_PX = 793

export const A4_CONTENT_HEIGHT_PX = 1036

export function printDayScale(measuredHeight: number): number {
  if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) return 1
  if (measuredHeight <= A4_CONTENT_HEIGHT_PX) return 1
  return (A4_CONTENT_HEIGHT_PX / measuredHeight) * 0.97
}

/**
 * The width the scaled sheet is drawn at.
 *
 * Legacy's own: `width: dayScale < 1 ? (100 / dayScale)% : '100%'`. A `transform: scale()` shrinks
 * the box as well as its contents, so without widening it first a scaled day prints in a column down
 * the left of the page.
 */
export function printDayWidth(scale: number): string {
  // A CSS length, not a number on a screen: `100,3%` is invalid CSS and nobody reads this string.
  // N-02 is about prose and prices. **The directive has to be the line directly above the code** -
  // it was above this explanation first, which lints the comment and leaves the `toFixed` reported.
  // eslint-disable-next-line no-restricted-syntax -- a CSS length; see above
  return scale < 1 ? `${(100 / scale).toFixed(3)}%` : '100%'
}
